/**
 * Manage every WhatsApp number linked by QR in this workspace (up to 5, beside the workspace's Meta number if it has
 * one). Each number is a card (name, phone, state) in a grid, like the platform cards on the Integrations page; opening a card shows that
 * number's details: name, state, scan a QR to (re)connect, disconnect, remove an unused one, its logins. Another number is added from the
 * dashed "Add number" card. Adding asks for the
 * PHONE NUMBER first so the server can refuse a number that is already linked or currently on Meta before any QR is
 * shown. Owner-only (Integrations is an owner page; the server enforces it too). Rendered by Integrations.jsx.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, IconButton, Typography, TextField,
  Chip, Stack, CircularProgress, Card,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { isValidPhoneNumber, parsePhoneNumberFromString } from "libphonenumber-js";
import { fetchWorkspaces } from "../../../redux/Slice/Workspace/workspaceSlice";
import { setConnectionFilter } from "../../../redux/Slice/Inbox/inboxRecentSlice";
import { useWhatsAppQrNumbers } from "../../../features/whatsapp/hooks/useWhatsAppQrNumbers";
import WhatsAppQrSessionsPanel from "./WhatsAppQrSessionsPanel";

const STATE_LABEL = {
  CONNECTED: { text: "Connected", color: "success" },
  QR_PENDING: { text: "Waiting for scan", color: "warning" },
  CREATING: { text: "Starting…", color: "warning" },
  AUTHENTICATING: { text: "Connecting…", color: "warning" },
  RECONNECTING: { text: "Reconnecting…", color: "warning" },
  DISCONNECTED: { text: "Disconnected", color: "default" },
  LOGGED_OUT: { text: "Logged out", color: "default" },
  STOPPED: { text: "Stopped", color: "default" },
  ERROR: { text: "Error", color: "error" },
};
const NEEDS_SCAN = new Set(["LOGGED_OUT", "STOPPED", "ERROR", "DISCONNECTED", null, undefined]);

// The numbers dialog is a grid of cards (one per number, like the platform cards on the Integrations page); a card opens that number's details
// (name, QR, buttons, logins) in the same dialog. A card shows the number's state as plain coloured text, with a faint wash in the same colour.
const STATE_TEXT_COLOR = { success: "success.main", warning: "warning.main", error: "error.main", default: "text.secondary" };
const STATE_TINT = { success: "#25D366", warning: "#ED6C02", error: "#D32F2F", default: "#9E9E9E" };
const CARD_MIN_HEIGHT = 210;

// The larger type scale the dialog's detail/add views have always used (it used to sit on DialogContent). The card grid is outside it, so the
// cards keep the Integrations page's own type scale.
const DETAIL_SCALE_SX = {
  "& .MuiTypography-root:not(.qr-name)": { fontSize: "1.1rem", lineHeight: 1.6 },
  "& .MuiButton-root": { fontSize: "1.1rem", px: 2.5, py: 1 },
  "& .MuiInputBase-input": { fontSize: "1.05rem", py: 1.6 },
  "& .MuiInputLabel-root": { fontSize: "1.05rem" },
  "& .MuiFormHelperText-root": { fontSize: "0.9rem" },
  "& .MuiChip-label": { fontSize: "0.95rem", px: 1.5 },
};

// Open on click, or on Enter / Space when the card itself (not a button inside it) has the focus.
const cardOpenProps = (onOpen) => ({
  role: "button",
  tabIndex: 0,
  onClick: onOpen,
  onKeyDown: (e) => {
    if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onOpen();
    }
  },
});

const NumberCard = ({ item, onOpen }) => {
  const meta = STATE_LABEL[item.state] || { text: item.state || "Unknown", color: "default" };
  const tint = STATE_TINT[meta.color] || STATE_TINT.default;
  const name = item.label || item.phoneNumber || `Number ${item.slot + 1}`;
  return (
    <Card
      variant="outlined"
      {...cardOpenProps(onOpen)}
      sx={{
        minWidth: 0,
        minHeight: CARD_MIN_HEIGHT,
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        cursor: "pointer",
        backgroundColor: "transparent",
        backgroundImage: (theme) => `linear-gradient(to bottom left, ${tint}${theme.palette.mode === "dark" ? "2E" : "22"} 0%, transparent 55%)`,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
        "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ p: 2.5, pb: 0, flex: 1, minWidth: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Typography title={name} noWrap sx={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.3, color: "text.primary", minWidth: 0 }}>
            {name}
          </Typography>
          <WhatsAppIcon sx={{ fontSize: 40, flexShrink: 0, color: tint }} />
        </Stack>
        {item.phoneNumber && (
          <Typography sx={{ mt: 0.75, fontSize: "1.0625rem", lineHeight: 1.5, color: "text.secondary" }}>
            +{String(item.phoneNumber).replace(/^\+/, "")}
          </Typography>
        )}
        {item.linkedToMeta && (
          <Typography sx={{ mt: 0.75, fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.4, color: "info.main" }}>
            Linked to Meta
          </Typography>
        )}
        {item.sessionCount > 0 && (
          <Typography sx={{ mt: 0.75, fontSize: "0.95rem", lineHeight: 1.4, color: "text.secondary" }}>
            {item.sessionCount + 1} logins
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          mx: 2.5,
          mt: 2,
          mb: 2,
          pt: 1.75,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: "1.0625rem", fontWeight: 700, color: STATE_TEXT_COLOR[meta.color] || STATE_TEXT_COLOR.default }}>
          {meta.text}
        </Typography>
        {/* Opens the same details as the card itself (the click bubbles up to it). */}
        <Button
          variant="outlined"
          sx={{ textTransform: "none", borderRadius: "8px", color: "text.primary", borderColor: "divider", fontSize: "1rem", fontWeight: 600, px: 2, py: 0.6, "&:hover": { borderColor: "text.secondary", backgroundColor: "action.hover" } }}
        >
          Details
        </Button>
      </Box>
    </Card>
  );
};

const AddNumberCard = ({ onOpen }) => (
  <Card
    variant="outlined"
    {...cardOpenProps(onOpen)}
    sx={{
      minWidth: 0,
      minHeight: CARD_MIN_HEIGHT,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.5,
      borderRadius: "12px",
      borderStyle: "dashed",
      cursor: "pointer",
      backgroundColor: "transparent",
      color: "text.secondary",
      transition: "border-color 0.2s, color 0.2s, background-color 0.2s",
      "&:hover": { borderColor: "primary.main", color: "primary.main", backgroundColor: "action.hover" },
      "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    }}
  >
    <AddIcon sx={{ fontSize: 38 }} />
    <Typography sx={{ fontSize: "1.15rem", fontWeight: 600, color: "inherit" }}>Add number</Typography>
  </Card>
);

// Same international-format rule as the Add contact form: a complete number with its country code. Returns the digits (no "+") or an error.
const checkNewQrPhone = (raw) => {
  const cleaned = String(raw || "").replace(/[\s\-()]/g, "");
  if (!cleaned) return { ok: false, message: "" };
  const candidate = cleaned.startsWith("+") ? cleaned : cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : /^\d+$/.test(cleaned) ? `+${cleaned}` : cleaned;
  const parsed = isValidPhoneNumber(candidate) ? parsePhoneNumberFromString(candidate) : null;
  if (!parsed || !parsed.isValid()) return { ok: false, message: "Enter a valid phone number in international format, with its country code (e.g. +923001234567)" };
  return { ok: true, digits: parsed.number.replace(/^\+/, "") };
};

const NumberRow = ({ item, workspaceId, maxSessions, onRename, onConnect, onLogout, onRequestUnlink, onRequestRemove }) => {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(item.label || "");
  const [busy, setBusy] = useState(false);
  const meta = STATE_LABEL[item.state] || { text: item.state || "Unknown", color: "default" };
  // The main login's own state. Equal to the number's state unless the number has extra sessions (multi-session, off by default).
  const mainState = item.mainSessionState || item.state;
  const showSessions = (maxSessions > 1 || item.sessionCount > 0) && !item.linkedToMeta;

  const guard = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (_) {
      // the hook already surfaced the message in the dialog's error banner
    } finally {
      setBusy(false);
    }
  };
  const saveLabel = () =>
    guard(async () => {
      await onRename(item.connectionId, label.trim());
      setEditing(false);
    });

  return (
    <Box sx={{ py: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        {editing ? (
          <>
            <TextField value={label} onChange={(e) => setLabel(e.target.value)} inputProps={{ maxLength: 60 }} autoFocus />
            <IconButton size="medium" onClick={saveLabel} disabled={busy || !label.trim()}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <Typography className="qr-name" sx={{ fontWeight: 600, fontSize: "1.3rem" }}>{item.label || item.phoneNumber || `Number ${item.slot + 1}`}</Typography>
            <IconButton size="medium" onClick={() => setEditing(true)} aria-label="Rename number">
              <EditIcon fontSize="small" />
            </IconButton>
          </>
        )}
        <Chip size="medium" label={meta.text} color={meta.color} variant="outlined" />
        {item.linkedToMeta && <Chip size="medium" label="Linked to Meta" color="info" variant="outlined" />}
      </Stack>
      {item.phoneNumber && (
        <Typography variant="body1" color="text.secondary">+{String(item.phoneNumber).replace(/^\+/, "")}</Typography>
      )}
      {mainState === "QR_PENDING" && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
          {item.qrDataUrl ? (
            <img src={item.qrDataUrl} alt="Scan to connect WhatsApp" style={{ width: 260, height: 260, borderRadius: 8, background: "#fff", padding: 8 }} />
          ) : (
            <CircularProgress size={24} />
          )}
          <Typography variant="body2" color="text.secondary" align="center">
            On this number&apos;s phone: WhatsApp → Settings → Linked Devices → Link a Device.
          </Typography>
        </Box>
      )}
      {/* The "This number is a companion of your Meta number ..." paragraph that used to be shown for a linked number was removed (owner
          decision, 2026-10-15): the "Linked to Meta" chip says it. Linking, Unlink and every rule behind them are unchanged. */}
      {/* The "Not linked to Meta: ..." explanation that used to be shown here was removed (owner decision, 2026-10-15): it made a working number
          look disconnected. The server still computes `linkHint` (qrNumberRules.diagnoseLinkHints) and returns it; nothing is shown. */}
      {item.lastErrorMessage && (mainState === "ERROR" || mainState === "LOGGED_OUT") && (
        <Typography variant="body2" color="error" sx={{ display: "block" }}>
          {item.mainSessionState ? "Main login: " : ""}{item.lastErrorMessage}
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        {NEEDS_SCAN.has(mainState) && (
          <Button size="large" variant="outlined" disabled={busy} onClick={() => guard(() => onConnect(item.connectionId))} sx={{ textTransform: "none" }}>
            {mainState === "LOGGED_OUT" || mainState == null ? "Connect (scan QR)" : "Reconnect"}
          </Button>
        )}
        {mainState !== "LOGGED_OUT" && (
          <Button size="large" color="error" disabled={busy} onClick={() => guard(() => onLogout(item.connectionId))} sx={{ textTransform: "none" }}>
            Disconnect
          </Button>
        )}
        {item.linkedToMeta && (
          <Button size="large" color="inherit" disabled={busy} onClick={() => onRequestUnlink(item)} sx={{ textTransform: "none" }}>
            Unlink from Meta
          </Button>
        )}
        {(item.state === "LOGGED_OUT" || item.state === "CREATING") && (
          // Frees the slot (e.g. after a refused scan). Asks for confirmation first (see the dialog below).
          <Button size="large" color="inherit" disabled={busy} onClick={() => onRequestRemove(item)} sx={{ textTransform: "none" }}>
            Remove
          </Button>
        )}
      </Stack>
      {showSessions && <WhatsAppQrSessionsPanel workspaceId={workspaceId} number={item} maxSessions={Math.max(maxSessions, (item.sessionCount || 0) + 1)} />}
    </Box>
  );
};

const WhatsAppQrNumbersDialog = ({ open, onClose, workspaceId }) => {
  const dispatch = useDispatch();
  const { connections, maxNumbers, maxSessions, canAddNumber, loading, error, addNumber, removeNumber, renameNumber, connectNumber, logoutNumber, unlinkFromMeta } =
    useWhatsAppQrNumbers(workspaceId, open);
  const selectedInboxNumber = useSelector((s) => s.inboxRecent?.selectedConnectionId);
  const [removeTarget, setRemoveTarget] = useState(null); // the number the owner is being asked to remove
  const [removing, setRemoving] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState(null); // the linked number the owner is being asked to unlink from Meta
  const [unlinking, setUnlinking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selectedId, setSelectedId] = useState(null); // the number whose card was opened (its details replace the grid); null = the grid
  const [newLabel, setNewLabel] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  // Errors of this dialog's actions (e.g. "already linked by QR") show as the app's normal top-right toast, not inside the dialog.
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // First visit with nothing linked yet: go straight to the add form.
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (open && !loading && !autoOpened && connections.length === 0 && canAddNumber) {
      setAdding(true);
      setAutoOpened(true);
    }
  }, [open, loading, autoOpened, connections.length, canAddNumber]);

  const handleClose = () => {
    setAutoOpened(false);
    setAdding(false);
    setSelectedId(null);
    setNewLabel("");
    setNewPhone("");
    dispatch(fetchWorkspaces()); // refresh the inbox filter / badges with any change made here
    onClose();
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      await removeNumber(removeTarget.connectionId);
      toast.success(`${removeTarget.label || "The number"} was removed.`);
      // Its chats are gone from the inbox now: reload the list (and drop the number filter if it was on that number).
      dispatch(setConnectionFilter(selectedInboxNumber && selectedInboxNumber !== removeTarget.connectionId ? selectedInboxNumber : null));
      dispatch(fetchWorkspaces());
      setRemoveTarget(null);
    } catch (_) {
      // the hook already showed the server's message in the dialog's banner
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget) return;
    setUnlinking(true);
    try {
      const out = await unlinkFromMeta(unlinkTarget.connectionId);
      toast.success(out?.metaDisconnected ? "Unlinked. Meta was disconnected for this number." : "Unlinked from Meta.");
      dispatch(fetchWorkspaces()); // the Meta card and the chat composers change with it
    } catch (_) {
      // the hook already showed the server's message in the dialog's banner
    } finally {
      setUnlinking(false);
      setUnlinkTarget(null);
    }
  };

  const submitAdd = async () => {
    setAddBusy(true);
    try {
      const checked = checkNewQrPhone(newPhone);
      if (!checked.ok) return; // the field already shows why; never start a QR for an incomplete number
      const out = await addNumber(newLabel.trim(), checked.digits);
      toast.success("Number added — scan its QR code to connect it.");
      setAdding(false);
      setNewLabel("");
      setNewPhone("");
      // Open the new number's card so its QR is shown right away (it used to appear inline in the list).
      if (out?.connection?.connectionId) setSelectedId(out.connection.connectionId);
    } catch (_) {
      // message shown in the error banner
    } finally {
      setAddBusy(false);
    }
  };

  // Which view the dialog shows: the add form, one number's details (its card was opened), or the grid of cards. A number that disappears
  // while its details are open (removed) simply falls back to the grid.
  const selectedNumber = selectedId ? connections.find((c) => c.connectionId === selectedId) || null : null;
  const view = loading ? "loading" : adding && canAddNumber ? "add" : selectedNumber ? "detail" : "list";
  const introText = (
    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
      Each number is linked like WhatsApp Web. Names are only for your team — customers never see them.
    </Typography>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { bgcolor: "background.paper", borderRadius: 3 } }}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "1.5rem", fontWeight: 700, px: 4, pt: 3 }}>
        WhatsApp numbers ({connections.length}/{maxNumbers})
        <IconButton onClick={handleClose} size="medium">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 4 }}>
        {loading ? (
          <>
            <Box sx={DETAIL_SCALE_SX}>{introText}</Box>
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} /></Box>
          </>
        ) : view === "add" ? (
          <Box sx={DETAIL_SCALE_SX}>
            <Button startIcon={<ArrowBackIcon />} disabled={addBusy} onClick={() => setAdding(false)} sx={{ textTransform: "none", ml: -1, mb: 0.5 }}>All numbers</Button>
            <Stack spacing={1.5} sx={{ py: 1 }}>
              <TextField fullWidth label="Phone number" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} inputProps={{ inputMode: "tel", maxLength: 20 }} autoFocus error={Boolean(newPhone) && !checkNewQrPhone(newPhone).ok} helperText={(Boolean(newPhone) && checkNewQrPhone(newPhone).message) || "International format with country code, e.g. +923001234567"} />
              <TextField fullWidth label="Name for this number (e.g. Sales)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} inputProps={{ maxLength: 60 }} />
              <Typography variant="body2" color="text.secondary">
                We check the number first. A number that is already linked, or currently connected to the official WhatsApp Business API, can&apos;t be added.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={addBusy || !newLabel.trim() || !checkNewQrPhone(newPhone).ok} onClick={submitAdd} sx={{ textTransform: "none" }}>Add</Button>
                <Button disabled={addBusy} onClick={() => setAdding(false)} sx={{ textTransform: "none" }}>Cancel</Button>
              </Stack>
            </Stack>
          </Box>
        ) : view === "detail" ? (
          // One number's details: name, state, QR, buttons and its logins (sessions) -- exactly what each row of the old list showed.
          <Box sx={DETAIL_SCALE_SX}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedId(null)} sx={{ textTransform: "none", ml: -1, mb: 0.5 }}>All numbers</Button>
            <NumberRow key={selectedNumber.connectionId} item={selectedNumber} workspaceId={workspaceId} maxSessions={maxSessions} onRename={renameNumber} onConnect={connectNumber} onLogout={logoutNumber} onRequestUnlink={setUnlinkTarget} onRequestRemove={setRemoveTarget} />
          </Box>
        ) : (
          <>
            <Box sx={DETAIL_SCALE_SX}>{introText}</Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                gap: 3,
                mt: 2,
                pb: 1,
              }}
            >
              {connections.map((c) => (
                <NumberCard key={c.connectionId} item={c} onOpen={() => setSelectedId(c.connectionId)} />
              ))}
              {canAddNumber && <AddNumberCard onOpen={() => setAdding(true)} />}
            </Box>
            <Box sx={DETAIL_SCALE_SX}>
              {connections.length === 0 && !canAddNumber && (
                <Typography variant="body1" color="text.secondary">No WhatsApp numbers linked yet.</Typography>
              )}
              {!canAddNumber && connections.length >= maxNumbers && (
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  You&apos;ve reached the maximum of {maxNumbers} numbers for this workspace.
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 4, pb: 3 }}>
        <Button size="large" onClick={handleClose} sx={{ textTransform: "none", fontSize: "1.05rem" }}>Done</Button>
      </DialogActions>

      <Dialog open={Boolean(unlinkTarget)} onClose={() => !unlinking && setUnlinkTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: "1.4rem", fontWeight: 700, px: 4, pt: 3 }}>Unlink from Meta</DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.6, mb: 1 }}>
            {unlinkTarget?.label || "This number"} will work as a normal QR number. New customer messages will arrive through QR as
            separate QR contacts, and your official Meta connection for this number will be disconnected so messages are not received twice.
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.6 }}>
            Existing chats stay as they are. This cannot be undone: to use Meta with this number again you would have to remove it from QR,
            connect it to Meta again, and then link it by QR.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3 }}>
          <Button size="large" onClick={() => setUnlinkTarget(null)} disabled={unlinking} sx={{ textTransform: "none", fontSize: "1.05rem" }}>Cancel</Button>
          <Button size="large" onClick={confirmUnlink} disabled={unlinking} color="error" variant="contained" sx={{ textTransform: "none", fontSize: "1.05rem" }}>
            {unlinking ? "Unlinking…" : "Unlink and disconnect Meta"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Same confirmation style as Delete Contact: says plainly that it cannot be undone. */}
      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontSize: "1.4rem", fontWeight: 700, px: 4, pt: 3 }}>Remove Number</DialogTitle>
        <DialogContent sx={{ px: 4 }}>
          <Typography variant="body1" sx={{ fontSize: "1.1rem", lineHeight: 1.6 }}>
            Are you sure you want to remove {removeTarget?.label || "this number"}? All its chats and contacts will be removed from your
            inbox and Contacts page. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 4, pb: 3 }}>
          <Button size="large" onClick={() => setRemoveTarget(null)} disabled={removing} sx={{ textTransform: "none", fontSize: "1.05rem" }}>Cancel</Button>
          <Button size="large" onClick={confirmRemove} disabled={removing} color="error" variant="contained" sx={{ textTransform: "none", fontSize: "1.05rem" }}>
            {removing ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default WhatsAppQrNumbersDialog;
